import { LOCALE_INFO, LOCALES, type LocaleCode } from '@amumin/i18n';
import { Icon, cx } from '@amumin/ui-core';

import styles from './language.module.css';

// Taalkeuze (golf 3, docs/DESIGN.md §8.T): zes rustige tegels met de naam van elke taal in haar
// eigen taal en schrift (Nederlands, English, Türkçe, العربية, Deutsch, Français). Elke tegel
// draagt zijn eigen lang en dir, zodat een schermlezer de naam goed uitspreekt en het Arabisch van
// rechts naar links staat, ook op een Nederlandse pagina. Grote tikvlakken (min. 64 px).

export interface LanguagePickerProps {
  /** Naam van de groep (toegankelijke naam en data-name van de tegels). */
  name: string;
  label: string;
  value: LocaleCode;
  onChange: (code: LocaleCode) => void;
  /** Legenda alleen voor schermlezers (als de titel dezelfde vraag al stelt). */
  hideLabel?: boolean;
  disabled?: boolean;
  /** 0065: in een smalle kaart (aanmelden) altijd twee kolommen. */
  narrow?: boolean;
}

export function LanguagePicker({
  name,
  label,
  value,
  onChange,
  hideLabel,
  disabled,
  narrow,
}: LanguagePickerProps) {
  return (
    <fieldset class={styles.picker} data-name={name} disabled={disabled}>
      <legend class={hideLabel ? 'fb-sr-only' : styles.legend}>{label}</legend>
      <div class={cx(styles.grid, narrow && styles.narrow)} role="radiogroup" aria-label={label}>
        {LOCALES.map((code) => {
          const info = LOCALE_INFO[code];
          const on = code === value;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={on ? 'true' : 'false'}
              class={cx(styles.tile, on && styles.on)}
              data-name={`${name}-${code}`}
              lang={info.lang}
              dir={info.dir}
              onClick={() => onChange(code)}
            >
              <span class={styles.native}>{info.nativeName}</span>
              <span class={styles.check} aria-hidden="true">
                {on ? <Icon id="circle-check" size={24} /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Naam van een taal in haar eigen schrift (voor "Zoals het gezin (Türkçe)"). */
export function nativeName(code: string | null | undefined): string {
  const info = LOCALES.indexOf(code as LocaleCode) >= 0 ? LOCALE_INFO[code as LocaleCode] : null;
  return info ? info.nativeName : LOCALE_INFO['nl-NL'].nativeName;
}
